class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Free gift management
    this.isProcessingGift = false;
    this.initializeFreeGift();
  }

  initializeFreeGift() {
    // Prevent multiple initializations (there might be multiple cart-drawer elements)
    if (window.freeGiftInitialized) {
      console.log('[CartDrawer] Free gift already initialized, skipping');
      return;
    }
    window.freeGiftInitialized = true;

    // Get free gift settings from the free-gift component
    const freeGiftElement = document.querySelector('free-gift');
    if (!freeGiftElement) {
      console.log('[CartDrawer] Free gift element not found');
      return;
    }

    this.freeGiftVariantId = freeGiftElement.dataset.freeGiftProductVariantId;
    this.freeGiftThreshold = Number(freeGiftElement.dataset.freeGiftThreshold);

    if (!this.freeGiftVariantId || !this.freeGiftThreshold) {
      console.log('[CartDrawer] Free gift not configured');
      return;
    }

    console.log('[CartDrawer] Free gift initialized', {
      variantId: this.freeGiftVariantId,
      threshold: this.freeGiftThreshold
    });

    this.updateProgressBarInitial();
    // Initialize manual removal tracking
    this.manualRemovalKey = `freeGift_manualRemoval_${this.freeGiftVariantId}`;
    this.lastCartTotalKey = `freeGift_lastTotal_${this.freeGiftVariantId}`;

    // Restore manual removal state from sessionStorage
    const storedRemovalState = sessionStorage.getItem(this.manualRemovalKey);
    this.manuallyRemoved = storedRemovalState === 'true';
    const storedLastTotal = sessionStorage.getItem(this.lastCartTotalKey);
    this.lastCartTotal = storedLastTotal ? Number(storedLastTotal) : 0;

    // Track whether gift was in cart (to detect manual removal)
    this.lastHadGift = false;

    console.log('[CartDrawer] Manual removal state:', {
      manuallyRemoved: this.manuallyRemoved,
      lastTotal: this.lastCartTotal
    });

    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleFreeGiftEligibility(event);
    });

    // Intercept cart remove button clicks to detect manual gift removal
    this.setupRemoveButtonInterceptor();

    // Listen for re-add requests from free-gift component
    window.addEventListener('freeGiftReAddRequested', async (e) => {
      console.log('[CartDrawer] Re-add gift requested by user, current manuallyRemoved:', this.manuallyRemoved);
      // Clear the manual removal flag
      this.manuallyRemoved = false;
      if (this.manualRemovalKey) {
        sessionStorage.removeItem(this.manualRemovalKey);
        console.log('[CartDrawer] Cleared manual removal flag from storage');
      }
      // Fetch fresh cart data and trigger eligibility check
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        console.log('[CartDrawer] Fetched fresh cart for re-add, calling handleFreeGiftEligibility');
        await this.handleFreeGiftEligibility({ cartData, source: 'user-re-add' });
      } catch (error) {
        console.error('[CartDrawer] Error fetching cart for re-add:', error);
      }
    });

    // Check eligibility on page load
    setTimeout(() => {
      this.checkFreeGiftOnLoad();
    }, 500);
  }

  updateProgressBarInitial() {
    document.querySelector('#shopify-section-cart-drawer').dataset.freeGiftProgress = document.querySelector('free-gift').dataset.progress;
  }

  setupRemoveButtonInterceptor() {
    // Set up event delegation for remove buttons
    document.addEventListener('click', (e) => {
      const removeButton = e.target.closest('cart-remove-button');
      if (!removeButton) return;

      const variantId = removeButton.querySelector('button')?.dataset.variantId;

      // Check if this is the free gift being removed
      if (variantId && variantId === String(this.freeGiftVariantId)) {
        console.log('[CartDrawer] User clicked remove on FREE GIFT!');
        this.manuallyRemoved = true;
        sessionStorage.setItem(this.manualRemovalKey, 'true');
        console.log('[CartDrawer] Gift marked as manually removed (via button click)');
      }
    });
  }

  async checkFreeGiftOnLoad() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      await this.handleFreeGiftEligibility({ cartData, source: 'initial-load' });
    } catch (error) {
      console.error('[CartDrawer] Error checking free gift on load:', error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  async handleFreeGiftEligibility(event) {
    this.updateProgressBarInitial();
    if (this.isProcessingGift) {
      console.log('[CartDrawer] Already processing gift, skipping');
      return;
    }
    // Always fetch fresh cart data to ensure accuracy
    let cartData;
    try {
      const response = await fetch('/cart.js');
      cartData = await response.json();
    } catch (error) {
      console.error('[CartDrawer] Error fetching cart:', error);
      return;
    }

    if (!cartData || !cartData.items) {
      console.log('[CartDrawer] No cart data available');
      return;
    }

    const currentTotal = cartData.total_price;


    // REMOVE DUPLICATES: Check for manually added free gift items (without _free_gift property)
    // We only want the auto-added version with the _free_gift property
    const duplicateGiftItems = cartData.items.filter(item =>
      item.variant_id === Number(this.freeGiftVariantId) &&
      (!item.properties || !item.properties._free_gift)
    );

    if (duplicateGiftItems.length > 0) {
      console.log('[CartDrawer] Found', duplicateGiftItems.length, 'manually added gift items without _free_gift property - removing them');
      for (const duplicateItem of duplicateGiftItems) {
        await this.removeFreeGift(duplicateItem.key, false);
      }
      // Fetch updated cart after removing duplicates
      try {
        const response = await fetch('/cart.js');
        cartData = await response.json();
      } catch (error) {
        console.error('[CartDrawer] Error fetching cart after removing duplicates:', error);
        return;
      }
    }

    // Check if gift is in cart
    const giftItem = cartData.items.find(item =>
      item.variant_id === Number(this.freeGiftVariantId) &&
      item.properties &&
      item.properties._free_gift
    );

    // DETECT MANUAL REMOVAL: Gift was eligible before but now it's gone
    if (!giftItem && !this.manuallyRemoved && this.lastHadGift) {
      console.log('[CartDrawer] Gift was manually removed by user!');
      this.manuallyRemoved = true;
      sessionStorage.setItem(this.manualRemovalKey, 'true');
    }

    // Track if we had the gift last time
    this.lastHadGift = !!giftItem;

    // Detect significant cart changes to clear manual removal block
    if (this.manuallyRemoved && this.lastCartTotal > 0) {
      const percentChange = Math.abs((currentTotal - this.lastCartTotal) / this.lastCartTotal) * 100;

      // If cart total changed by more than 10%, or went to 0 and back, clear the block
      if (percentChange > 10 || (this.lastCartTotal === 0 && currentTotal > 0)) {
        console.log('[CartDrawer] Cart changed significantly (', percentChange.toFixed(1), '%), clearing manual removal block');
        this.manuallyRemoved = false;
        sessionStorage.removeItem(this.manualRemovalKey);
      }
    }

    console.log('[CartDrawer] Checking gift eligibility', {
      source: event.source,
      itemCount: cartData.item_count,
      totalPrice: currentTotal,
      manuallyRemoved: this.manuallyRemoved,
      hadGift: this.lastHadGift
    });

    // Calculate total excluding gift items (only the legitimate auto-added ones with _free_gift property)
    const totalWithoutGift = cartData.items.reduce((sum, item) => {
      if (item.properties && item.properties._free_gift) {
        console.log('[CartDrawer] Found gift item:', item.title);
        return sum;
      }
      return sum + item.final_line_price;
    }, 0);


    const isEligible = totalWithoutGift >= this.freeGiftThreshold;

    console.log('[CartDrawer] Eligibility:', {
      totalWithoutGift,
      threshold: this.freeGiftThreshold,
      isEligible,
      hasGift: !!giftItem,
      manuallyRemoved: this.manuallyRemoved
    });

    // Update free-gift component about gift status
    const freeGiftElement = document.querySelector('free-gift');
    if (freeGiftElement && freeGiftElement.updateGiftStatus) {
      freeGiftElement.updateGiftStatus(!!giftItem);
    }

    // Update last known total
    this.lastCartTotal = currentTotal;
    sessionStorage.setItem(this.lastCartTotalKey, currentTotal);

    // Only add gift if eligible AND not manually removed
    console.log('[CartDrawer] Condition check - isEligible:', isEligible, '| hasGift:', !!giftItem, '| manuallyRemoved:', this.manuallyRemoved);
    if (isEligible && !giftItem && !this.manuallyRemoved) {
      console.log('[CartDrawer] ✓ All conditions met - Adding free gift');
      await this.addFreeGift();
    } else if (isEligible && !giftItem && this.manuallyRemoved) {
      console.log('[CartDrawer] ✗ Gift blocked - manuallyRemoved flag is still true!');
    } else if (!isEligible) {
      console.log('[CartDrawer] ✗ User not eligible for gift');
    } else if (giftItem) {
      console.log('[CartDrawer] ✗ Gift already in cart');
    }

    if (!isEligible && giftItem) {
      console.log('[CartDrawer] Removing free gift (below threshold)');
      await this.removeFreeGift(giftItem.key, false);  // false = automatic removal, not manual
      // Clear manual removal flag when going below threshold
      this.manuallyRemoved = false;
      sessionStorage.removeItem(this.manualRemovalKey);
    }
  }

  async addFreeGift() {
    if (this.isProcessingGift) return;

    this.isProcessingGift = true;
    console.log('[CartDrawer] Adding gift, variant ID:', this.freeGiftVariantId);

    // Add class to indicate gift is being added
    this.addFreeGiftClass('is-adding-gift');

    // Show "Adding free gift..." message
    const freeGiftElement = document.querySelector('free-gift');
    if (freeGiftElement && freeGiftElement.showAddingMessage) {
      freeGiftElement.showAddingMessage();
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.freeGiftVariantId,
          quantity: 1,
          properties: { _free_gift: 'true' }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add gift');
      }

      const result = await response.json();
      console.log('[CartDrawer] Gift added:', result);

      // Mark that we now have the gift (so removals will be detected as manual)
      this.lastHadGift = true;

      // Remove adding class and add added class
      this.removeFreeGiftClass('is-adding-gift');
      this.addFreeGiftClass('gift-added');

      // Trigger cart refresh
      await this.refreshCartDrawer();
    } catch (error) {
      console.error('[CartDrawer] Error adding gift:', error);
      // Remove adding class on error
      this.removeFreeGiftClass('is-adding-gift');
      // Hide the adding message if there was an error
      if (freeGiftElement && freeGiftElement.hideAddingMessage) {
        freeGiftElement.hideAddingMessage();
      }
    } finally {
      this.isProcessingGift = false;
    }
  }

  async removeFreeGift(itemKey, isManualRemoval = true) {
    if (this.isProcessingGift) return;

    this.isProcessingGift = true;
    console.log('[CartDrawer] Removing gift, key:', itemKey, 'manual:', isManualRemoval);

    // Add class to indicate gift is being removed
    this.addFreeGiftClass('is-removing-gift');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove gift');
      }

      const result = await response.json();
      console.log('[CartDrawer] Gift removed:', result);

      // Mark as manually removed if this removal was user-initiated
      if (isManualRemoval) {
        console.log('[CartDrawer] Marking gift as manually removed');
        this.manuallyRemoved = true;
        sessionStorage.setItem(this.manualRemovalKey, 'true');
        this.addFreeGiftClass('gift-manually-removed');
      } else {
        this.addFreeGiftClass('gift-auto-removed');
      }

      // Remove the is-removing-gift class after a brief delay
      setTimeout(() => {
        this.removeFreeGiftClass('is-removing-gift');
      }, 300);

      // Trigger cart refresh
      await this.refreshCartDrawer();
    } catch (error) {
      console.error('[CartDrawer] Error removing gift:', error);
      // Remove the removing class on error
      this.removeFreeGiftClass('is-removing-gift');
    } finally {
      this.isProcessingGift = false;
    }
  }

  async refreshCartDrawer() {
    try {
      console.log('[CartDrawer] Refreshing drawer...');

      // Simple refresh: fetch the drawer section HTML and replace the sections returned by getSectionsToRender
      const cartDrawerResponse = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const cartDrawerHtml = await cartDrawerResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartDrawerHtml, 'text/html');

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        const newContent = doc.querySelector(section.selector || `#${section.id}`);
        if (newContent) {
          sectionElement.innerHTML = newContent.innerHTML;
        }
      });

      // Re-attach overlay click
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      }, 100);

      console.log('[CartDrawer] Drawer refreshed');
    } catch (error) {
      console.error('[CartDrawer] Error refreshing drawer:', error);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Restore simple renderContents: inject sections and open drawer
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector || '.shopify-section');
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    if (selector) {
      const parent = document.getElementById('shopify-section-cart-drawer');
      const rawProgress = parent && (parent.getAttribute('data-free-gift-progress') || parent.dataset.freeGiftProgress);

      if (rawProgress != null) {
        const progressValue = String(rawProgress).trim().replace(/%$/, '');
        const container = doc.querySelector(selector) || doc.body;
        const bar = container.querySelector('progress-bar'); // only one expected

        if (bar) {
          const existingStyle = bar.getAttribute('style') || '';
          let newStyle;

          if (/--progress:\s*[\d.]+%/i.test(existingStyle)) {
            newStyle = existingStyle.replace(/(--progress:\s*)[\d.]+%/i, `$1${progressValue}%`);
          } else {
            const trimmed = existingStyle.trim().replace(/\s*;?\s*$/, '');
            newStyle = trimmed ? `${trimmed}; --progress: ${progressValue}%;` : `--progress: ${progressValue}%;`;
          }

          bar.setAttribute('style', newStyle);
        }
      }
    }

    const node = doc.querySelector(selector);
    return node ? node.innerHTML : html;
    this.updateProgressBarInitial();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  addFreeGiftClass(className) {
    const freeGiftElement = document.querySelector('free-gift');
    if (freeGiftElement) {
      freeGiftElement.classList.add(className);
      console.log('[CartDrawer] Added class:', className);
    }
    this.updateProgressBarInitial();
  }

  removeFreeGiftClass(className) {
    const freeGiftElement = document.querySelector('free-gift');
    if (freeGiftElement) {
      freeGiftElement.classList.remove(className);
      console.log('[CartDrawer] Removed class:', className);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
